import { add, intervalToDuration } from 'date-fns';
import { Response as Application } from 'api/typegen/client/application/get';
import { Response as Client } from 'api/typegen/client/get';
import { RouteTrackingEventPayload } from 'hooks/eventCollector/payloads';
import {
  SESSION_PREV_ROUTE,
  SESSION_TIME_ROUTE,
  SESSION_WAITING_FLOW_TIME,
  SESSION_WAITING_SUBMIT_TEMP,
  SESSION_WAITING_SUBMIT_TIME,
  TRACKING_TIME,
} from 'utils/constants';
import { WAITING_ROUTES } from '../routes';
import fetchEventCollector from 'hooks/eventCollector/fetchEventCollector';

type TimeDetails = { hours?: number; minutes?: number; seconds?: number };

// - - - - Utils - - - -
const DEFAULT_ROUTE_TIME = '0h0m0s';
const NO_REQUIRED = 'no-required';

const isMethodTrackable = (callMethod: 'get' | 'post' | 'put' | 'delete') =>
  callMethod === 'post';

const getPrevRoute = () => sessionStorage.getItem(SESSION_PREV_ROUTE) || '/';

const isLoadingScreen = (prevRoute: string) => {
  const waitingRoutesValues = Object.values(WAITING_ROUTES);
  return waitingRoutesValues.includes(prevRoute);
};

// - - - - Parse Time data - - - -
const parseTimeForTracking = ({
  hours = 0,
  minutes = 0,
  seconds = 0,
}: TimeDetails = {}) => `${hours}h${minutes}m${seconds}s`;

const parseTimeSession = (time: string | null | undefined) => {
  const timeWithSpaces = (time || DEFAULT_ROUTE_TIME)
    .replace('h', ' ')
    .replace('m', ' ')
    .replace('s', ' ');
  const [hours = 0, minutes = 0, seconds = 0] = timeWithSpaces.split(' ');
  return {
    hours: Number(hours),
    minutes: Number(minutes),
    seconds: Number(seconds),
  };
};

// - - - - Track submit calls  - - - -
const preTrackingCall = (callMethod: 'get' | 'post' | 'put' | 'delete') => {
  if (!isMethodTrackable(callMethod)) return;
  sessionStorage.setItem(
    SESSION_WAITING_SUBMIT_TEMP,
    new Date().getTime().toString(),
  );
};

const postTrackingCall = (callMethod: 'get' | 'post' | 'put' | 'delete') => {
  if (!isMethodTrackable(callMethod)) return;

  const startTime =
    Number(sessionStorage.getItem(SESSION_WAITING_SUBMIT_TEMP)) || 0;

  const time = intervalToDuration({
    start: new Date(startTime),
    end: new Date(),
  });

  addTime(SESSION_WAITING_SUBMIT_TIME, {
    hours: time.hours || 0,
    minutes: time.minutes || 0,
    seconds: time.seconds || 0,
  });

  sendDataForTracking({ typeEvent: 'submit' });
};

// - - - - Add and update time - - - -
const addTime = (sessionKey: string, moreTime?: TimeDetails) => {
  // Get current time
  const time = sessionStorage.getItem(sessionKey); // format: {hours}h{minutes}m{seconds}s
  const timeParsed = parseTimeSession(time);

  const base = new Date(0);

  const afterInitial = add(base, {
    hours: timeParsed.hours || 0,
    minutes: timeParsed.minutes || 0,
    seconds: timeParsed.seconds || 0,
  });

  const afterAddition = add(afterInitial, {
    hours: moreTime?.hours || 0,
    minutes: moreTime?.minutes || 0,
    seconds: moreTime?.seconds || 0,
  });

  const resultTime = intervalToDuration({
    start: base,
    end: afterAddition,
  });

  const resultTimeParsed = parseTimeForTracking(resultTime);
  sessionStorage.setItem(sessionKey, resultTimeParsed);
};

// - - - - Get current flow and route time according to "now" date - - - -
const getFlowAndPrevRouteTime = () => {
  const newStartDate = new Date().getTime() || 0;

  const routeDate = Number(sessionStorage.getItem(SESSION_TIME_ROUTE)) || 0;
  const flowDate = Number(sessionStorage.getItem(TRACKING_TIME)) || 0;
  const prevRouteTime = intervalToDuration({
    start: new Date(routeDate),
    end: new Date(newStartDate),
  });

  const flowTime = intervalToDuration({
    start: new Date(flowDate),
    end: new Date(newStartDate),
  });

  return { prevRouteTime, flowTime };
};

// - - - - Function to send data to the BE - - - -
const sendDataForTracking = ({
  details = NO_REQUIRED,
  typeEvent,
  client,
  application,
}: {
  details?: string;
  typeEvent: 'screen' | 'error' | 'submit' | 'event';
  client?: Client | null | undefined;
  application?: Application | null | undefined;
}) => {
  const prevRoute = getPrevRoute();
  const { prevRouteTime, flowTime } = getFlowAndPrevRouteTime();

  const isTypeScreen = typeEvent === 'screen';

  const eventBody: RouteTrackingEventPayload = {
    route: window.location.pathname,
    prevRoute: isTypeScreen ? prevRoute : NO_REQUIRED,
    prevRouteTime: isTypeScreen
      ? parseTimeForTracking(prevRouteTime)
      : NO_REQUIRED,
    flowTime: parseTimeForTracking(flowTime),
    loadingTime:
      sessionStorage.getItem(SESSION_WAITING_FLOW_TIME) || DEFAULT_ROUTE_TIME,
    loadingSubmitTime:
      sessionStorage.getItem(SESSION_WAITING_SUBMIT_TIME) || DEFAULT_ROUTE_TIME,
    details,
    typeEvent: `ROUTE_TRACKING_${typeEvent}`,
    clientId: client?.id, // BI need it to get all client data
    applicationId: application?.id, // BI need it to get all Application data
  };
  if (import.meta.env.VITE_ENV === 'production') {
    fetchEventCollector(eventBody);
  }
};

// - - - - Function to inititalize + send data to the BE when client moves to another route - - - -

const initializeTrackingRoute = () => {
  const startDate = new Date().getTime().toString();
  sessionStorage.setItem(TRACKING_TIME, startDate);
  sessionStorage.setItem(SESSION_TIME_ROUTE, startDate);
};

const sendScreenTracking = ({
  client,
  application,
}: {
  client?: Client | null | undefined;
  application?: Application | null | undefined;
}) => {
  const prevRoute = getPrevRoute();

  const { prevRouteTime } = getFlowAndPrevRouteTime();

  if (isLoadingScreen(prevRoute)) {
    addTime(SESSION_WAITING_FLOW_TIME, {
      hours: prevRouteTime.hours,
      minutes: prevRouteTime.minutes,
      seconds: prevRouteTime.seconds,
    });
  }

  sendDataForTracking({ typeEvent: 'screen', client, application });
};

const trackingNewAvailableRoute = ({
  client,
  application,
}: {
  client?: Client | null | undefined;
  application?: Application | null | undefined;
}) => {
  const startFlowDate = sessionStorage.getItem(TRACKING_TIME);
  if (!startFlowDate) {
    initializeTrackingRoute();
    return;
  }
  sendScreenTracking({ client, application });
  sessionStorage.setItem(SESSION_TIME_ROUTE, new Date().getTime().toString());
};

// - - - - Functions exported for guardRoute+fetchData+axiosInstance - - - -
export {
  trackingNewAvailableRoute,
  preTrackingCall,
  postTrackingCall,
  sendDataForTracking,
  isMethodTrackable,
};
